<template>
  <nav
    class="breadcrumbs"
    :aria-label="alt || 'You are here'"
    role="navigation"
  >
    <ol class="bc-items">
      <li
        v-if="showHome"
        class="bc-item"
        :class="['bc-item--' + activeStyle]"
      >
        <router-link to="/">
          <span class="sr-only">Home</span>
          <Icon
            type="home"
            class="bc-item-label bc-item--has-link"
            aria-hidden="true"
          />
          <Icon
            type="chevron-right"
            class="bc-divider"
            aria-hidden="true"
          />
        </router-link>
      </li>
      <li
        v-for="(item, index) in items"
        :key="item"
        class="bc-item"
        :class="['bc-item--' + activeStyle]"
      >
        <router-link
          v-if="item.to && index < items.length - 1"
          :to="item.to"
          class="flex items-center"
        >
          <span class="sr-only">Navigate to {{ item.label }}</span>
          <Icon
            v-if="index > 0"
            type="chevron-right"
            class="bc-divider"
            aria-hidden="true"
          />
          <div class="bc-item-label bc-item--has-link">{{ item.label }}</div>
        </router-link>
        <div
          v-else
          class="flex items-center"
          :aria-current="index === items.length - 1 ? 'page' : undefined"
        >
          <Icon
            v-if="index > 0"
            type="chevron-right"
            class="bc-divider"
            aria-hidden="true"
          />
          <div
            class="bc-item-label"
            :class="{ 'bc-item--has-link': item.to && index < items.length - 1 }"
          >
            {{ item.label }}
          </div>
        </div>
      </li>
    </ol>
  </nav>
</template>

<script setup>
import Icon from '@/components/icon/Icon.vue'

defineProps({
  items: {
    type: Array,
    default: () => []
  },
  divider: {
    type: String,
    default: 'chevron'
  },
  showHome: {
    type: Boolean,
    default: true
  },
  activeStyle: {
    type: String,
    default: 'button'
  },
  alt: {
    type: String,
    default: null
  }
})
</script>

<style>
.breadcrumbs > .bc-items {
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: center;
  row-gap: var(--s-1);
}
.breadcrumbs .bc-item {
  color: var(--text-tertiary);
  font-weight: var(--font-medium);
}
.breadcrumbs .bc-divider {
  color: var(--text-quaternary);
  font-size: var(--text-sm);
  margin: 0 var(--s-1);
}
.breadcrumbs .bc-item-label {
  padding: var(--s-1) var(--s-2);
  border-radius: var(--rounded-md);
}
.breadcrumbs .bc-item-label.bc-item--has-link:hover {
  cursor: pointer;
}
.breadcrumbs .bc-item:not(.bc-item--text) .bc-item-label.bc-item--has-link:hover {
  background-color: var(--bg-tertiary);
}
.breadcrumbs .bc-item.bc-item--text .bc-item-label.bc-item--has-link:hover {
  color: var(--text-brand);
}

/* Last item */
.breadcrumbs .bc-item:last-child.bc-item--text .bc-item-label {
  color: var(--text-brand);
  font-weight: var(--font-semibold);
}
.breadcrumbs .bc-item:last-child.bc-item--button .bc-item-label {
  background-color: var(--bg-secondary);
  font-weight: var(--font-semibold);
}
.breadcrumbs .bc-item:last-child.bc-item--button-brand .bc-item-label {
  background-color: var(--bg-brand);
  color: var(--text-brand);
  font-weight: var(--font-semibold);
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
